// ================================================================
// src/freeStudy/PurchaseUI.jsx
// همه‌ی UI مرتبط با خرید و قفل + پلیر ویدیو
// ================================================================
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { formatPrice, toPersianDigits, computeVideoPrice, getSmartSuggestion } from './core.js';
import './PurchaseUI.css';

// ================================================================
// 1) PricingCard — کارت قیمت بالای کارت پیشرفت
// ================================================================
export function PricingCard({ course, pricing, onBuyBundle, isProcessing }) {
  if (!course || !pricing) return null;

  // حالت C: همه باز
  if (pricing.isComplete) {
    return (
      <div className="fsp-pricing-card fsp-pricing-complete">
        <div className="fsp-pricing-icon">🏆</div>
        <div className="fsp-pricing-body">
          <div className="fsp-pricing-title">همه‌ی ویدیوها باز است</div>
          <div className="fsp-pricing-sub">
            با موفقیت به همه‌ی {toPersianDigits(pricing.totalVideos)} ویدیو دسترسی دارید
          </div>
        </div>
      </div>
    );
  }

  const percent = pricing.totalVideos
    ? Math.round((pricing.ownedCount / pricing.totalVideos) * 100)
    : 0;

  // حالت A: کاربر جدید
  if (pricing.totalPaid === 0) {
    return (
      <div className="fsp-pricing-card fsp-pricing-new">
        <div className="fsp-pricing-glow" />
        <div className="fsp-pricing-head">
          <span className="fsp-pricing-badge">🔓 باز کردن کل دوره</span>
        </div>
        <div className="fsp-pricing-title-main">{course.title}</div>
        <div className="fsp-pricing-meta">
          <span>
            <i className="fas fa-video" /> {toPersianDigits(pricing.totalVideos)} ویدیو
          </span>
          <span>
            <i className="fas fa-lock-open" /> دسترسی دائمی
          </span>
        </div>
        <div className="fsp-pricing-price-row">
          <div>
            <div className="fsp-pricing-label">قیمت کل</div>
            <div className="fsp-pricing-price">
              {formatPrice(pricing.bundlePrice, course.currency)}
            </div>
          </div>
          <button
            className="fsp-pricing-cta"
            onClick={onBuyBundle}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <i className="fas fa-spinner fa-spin" /> در حال پردازش...
              </>
            ) : (
              <>
                <i className="fas fa-unlock" /> باز کردن کل دوره
              </>
            )}
          </button>
        </div>
        <div className="fsp-pricing-foot">
          با یک پرداخت، همه‌ی ویدیوها برای همیشه باز می‌شود
        </div>
      </div>
    );
  }

  // حالت B: کاربر جزئی
  return (
    <div className="fsp-pricing-card fsp-pricing-partial">
      <div className="fsp-pricing-glow" />
      <div className="fsp-pricing-head">
        <span className="fsp-pricing-badge">💎 دوره را کامل کن</span>
      </div>
      <div className="fsp-pricing-title-main">
        {toPersianDigits(pricing.ownedCount)} از{' '}
        {toPersianDigits(pricing.totalVideos)} ویدیو باز شده
      </div>
      <div className="fsp-pricing-progress-track">
        <div
          className="fsp-pricing-progress-fill"
          style={{ width: `${percent}%` }}
        />
      </div>
      <div className="fsp-pricing-price-row">
        <div>
          <div className="fsp-pricing-label">مبلغ باقی‌مانده</div>
          <div className="fsp-pricing-price">
            {pricing.bundlePrice === 0
              ? 'رایگان'
              : formatPrice(pricing.bundlePrice, course.currency)}
          </div>
        </div>
        <button
          className="fsp-pricing-cta"
          onClick={onBuyBundle}
          disabled={isProcessing}
        >
          {isProcessing ? (
            <>
              <i className="fas fa-spinner fa-spin" /> در حال پردازش...
            </>
          ) : (
            <>
              <i className="fas fa-unlock" /> تکمیل دوره
            </>
          )}
        </button>
      </div>
      <div className="fsp-pricing-foot">
        شما تا کنون {formatPrice(pricing.totalPaid, course.currency)} پرداخت کرده‌اید
      </div>
    </div>
  );
}

// ================================================================
// 2) VideoLockBadge — نشانگر قفل روی کارت ویدیو
// ================================================================
export function VideoLockBadge({ price, currency }) {
  return (
    <div className="fsp-lock-badge">
      <i className="fas fa-lock" />
      <span>{formatPrice(price, currency)}</span>
    </div>
  );
}

// ================================================================
// 3) PurchaseModal — مودال خرید ۴ فاز
// ================================================================
export function PurchaseModal({
  open,
  onClose,
  mode, // 'video' | 'bundle'
  course,
  video, // فقط وقتی mode='video'
  pricing,
  onConfirm,
  isProcessing,
  result,
}) {
  const [phase, setPhase] = useState('confirm');

  useEffect(() => {
    if (!open) {
      setPhase('confirm');
      return;
    }
    if (isProcessing) setPhase('processing');
    else if (result?.ok) setPhase('success');
    else if (result && !result.ok) setPhase('fail');
    else setPhase('confirm');
  }, [open, isProcessing, result]);

  if (!open) return null;

  const title =
    mode === 'bundle' ? 'باز کردن کل دوره' : 'باز کردن ویدیو';
  const itemTitle = mode === 'bundle' ? course?.title : video?.title;
  const price =
    mode === 'bundle' ? pricing?.bundlePrice : computeVideoPrice(video);

  return (
    <div className="fsp-modal-overlay" onClick={(e) => e.target === e.currentTarget && !isProcessing && onClose()}>
      <div className="fsp-modal">
        {phase === 'confirm' && (
          <>
            <div className="fsp-modal-header">
              <div>
                <div className="fsp-modal-eyebrow">
                  {mode === 'bundle' ? '💎 بسته‌ی کامل' : '🎬 ویدیوی تک'}
                </div>
                <h2 className="fsp-modal-title">{title}</h2>
              </div>
              <button className="fsp-modal-close" onClick={onClose}>
                <i className="fas fa-times" />
              </button>
            </div>

            <div className="fsp-modal-body">
              <div className="fsp-modal-item">
                <div className="fsp-modal-item-title">{itemTitle}</div>
                {mode === 'bundle' && (
                  <div className="fsp-modal-item-sub">
                    {toPersianDigits(pricing?.remainingCount || 0)} ویدیو باز می‌شود
                  </div>
                )}
              </div>

              {mode === 'bundle' && pricing?.totalPaid > 0 && (
                <div className="fsp-modal-note">
                  <i className="fas fa-info-circle" />
                  مبلغ زیر با احتساب پرداخت‌های قبلی شما محاسبه شده است
                </div>
              )}

              <div className="fsp-modal-price-row">
                <span>مبلغ قابل پرداخت</span>
                <strong>{formatPrice(price, course?.currency)}</strong>
              </div>
            </div>

            <div className="fsp-modal-actions">
              <button
                className="fsp-modal-btn fsp-modal-btn-ghost"
                onClick={onClose}
              >
                انصراف
              </button>
              <button
                className="fsp-modal-btn fsp-modal-btn-primary"
                onClick={onConfirm}
              >
                <i className="fas fa-credit-card" /> پرداخت
              </button>
            </div>
          </>
        )}

        {phase === 'processing' && (
          <div className="fsp-modal-processing">
            <div className="fsp-spinner" />
            <div className="fsp-modal-processing-title">
              در حال اتصال به درگاه پرداخت...
            </div>
            <div className="fsp-modal-processing-sub">
              لطفاً صفحه را نبندید
            </div>
          </div>
        )}

        {phase === 'success' && (
          <div className="fsp-modal-success">
            <div className="fsp-modal-success-icon">✅</div>
            <div className="fsp-modal-success-title">پرداخت موفق</div>
            <div className="fsp-modal-success-sub">
              {result?.message || 'قفل ویدیوها باز شد'}
            </div>
            <button
              className="fsp-modal-btn fsp-modal-btn-primary"
              onClick={onClose}
            >
              <i className="fas fa-play" /> شروع تماشا
            </button>
          </div>
        )}

        {phase === 'fail' && (
          <div className="fsp-modal-fail">
            <div className="fsp-modal-fail-icon">❌</div>
            <div className="fsp-modal-fail-title">پرداخت ناموفق</div>
            <div className="fsp-modal-fail-sub">
              {result?.message || 'لطفاً دوباره تلاش کنید'}
            </div>
            <div className="fsp-modal-actions">
              <button
                className="fsp-modal-btn fsp-modal-btn-ghost"
                onClick={onClose}
              >
                بستن
              </button>
              <button
                className="fsp-modal-btn fsp-modal-btn-primary"
                onClick={() => {
                  onClose();
                }}
              >
                <i className="fas fa-redo" /> تلاش دوباره
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ================================================================
// 4) ReceiptView — نمایش تاریخچه‌ی تراکنش‌ها
// ================================================================
export function ReceiptView({ open, onClose, transactions, currency }) {
  if (!open) return null;

  return (
    <div className="fsp-modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="fsp-modal fsp-receipt">
        <div className="fsp-modal-header">
          <h2 className="fsp-modal-title">📜 تاریخچه‌ی خریدها</h2>
          <button className="fsp-modal-close" onClick={onClose}>
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="fsp-modal-body">
          {(!transactions || transactions.length === 0) && (
            <div className="fsp-receipt-empty">هنوز خریدی ثبت نشده است</div>
          )}
          {transactions?.map((tx) => (
            <div key={tx.id} className="fsp-receipt-row">
              <div className="fsp-receipt-icon">
                {tx.type === 'PURCHASE_BUNDLE' ? '💎' : tx.type === 'GRANT' ? '🎁' : '🎬'}
              </div>
              <div className="fsp-receipt-info">
                <div className="fsp-receipt-title">
                  {tx.type === 'PURCHASE_BUNDLE'
                    ? 'خرید بسته‌ی کل دوره'
                    : tx.type === 'GRANT'
                    ? 'باز کردن رایگان'
                    : 'خرید ویدیو'}
                </div>
                <div className="fsp-receipt-date">
                  {new Date(tx.ts).toLocaleDateString('fa-IR')}
                </div>
              </div>
              <div className="fsp-receipt-amount">
                {tx.amount === 0 ? 'رایگان' : formatPrice(tx.amount, currency)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ================================================================
// 5) VideoOverlay — پلیر ویدیو (منتقل‌شده از FreeStudyCourse.jsx)
// ================================================================
export function VideoOverlay({
  course,
  videoIndex,
  video,
  isWatched,
  isLocked,
  notes,
  onClose,
  onWatched,
  onUnwatch,
  onSaveNote,
  onNext,
  onPrev,
  hasNext,
  hasPrev,
  onRequestPurchase,
}) {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [played, setPlayed] = useState(0);
  const [buffered, setBuffered] = useState(0); // 🆕 برای نمایش بافر
  const [volume, setVolume] = useState(0.8);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showRateMenu, setShowRateMenu] = useState(false);
  const [autoMarked, setAutoMarked] = useState(false);
  const [noteDraft, setNoteDraft] = useState(notes);
  const [noteSaved, setNoteSaved] = useState(false);

  // 🆕 استیت و رفرنس‌های مربوط به مخفی شدن نوار کنترلی
  const [controlsVisible, setControlsVisible] = useState(true);
  const hideControlsTimeoutRef = useRef(null);
  const progressBarRef = useRef(null);

  useEffect(() => {
    if (noteDraft === notes) return;
    const t = setTimeout(() => {
      onSaveNote(noteDraft);
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 1500);
    }, 800);
    return () => clearTimeout(t);
  }, [noteDraft, notes, onSaveNote]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // 🆕 مدیریت تایمر برای مخفی کردن خودکار نوار کنترلی
  const resetHideTimer = useCallback(() => {
    if (hideControlsTimeoutRef.current) clearTimeout(hideControlsTimeoutRef.current);
    if (isPlaying) {
      hideControlsTimeoutRef.current = setTimeout(() => {
        setControlsVisible(false);
      }, 1500);
    }
  }, [isPlaying]);

  useEffect(() => {
    if (controlsVisible && isPlaying) {
      resetHideTimer();
    } else {
      if (hideControlsTimeoutRef.current) clearTimeout(hideControlsTimeoutRef.current);
    }
    return () => {
      if (hideControlsTimeoutRef.current) clearTimeout(hideControlsTimeoutRef.current);
    };
  }, [controlsVisible, isPlaying, resetHideTimer]);

  // 🆕 کلیک روی ویدیو فقط نوار را تاگل می‌کند (نه پلی/پاز)
  const handleVideoClick = () => {
    setControlsVisible(prev => !prev);
    if (hideControlsTimeoutRef.current) clearTimeout(hideControlsTimeoutRef.current);
  };

  // 🆕 محاسبه میزان دانلود (بافر) شده
  const handleProgress = () => {
    if (videoRef.current && videoRef.current.buffered.length > 0) {
      setBuffered(videoRef.current.buffered.end(videoRef.current.buffered.length - 1));
    }
  };

  // 🆕 کلیک روی نوار پیشرفت سفارشی
  const handleProgressClick = (e) => {
    if (isLocked || !videoRef.current || !progressBarRef.current) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const newTime = pos * duration;
    videoRef.current.currentTime = newTime;
    setPlayed(newTime);
    resetHideTimer();
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      const t = e.target;
      const tag = (t?.tagName || '').toUpperCase();
      const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || t?.isContentEditable;
      if (isEditable) return;

      if (e.key === ' ' || e.key === 'k') {
        e.preventDefault();
        togglePlay();
      } else if (e.key === 'ArrowRight') {
        if (videoRef.current) videoRef.current.currentTime -= 5;
      } else if (e.key === 'ArrowLeft') {
        if (videoRef.current) videoRef.current.currentTime += 5;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, isLocked]);

  const togglePlay = () => {
    if (!videoRef.current || isLocked) return;
    if (videoRef.current.paused) videoRef.current.play();
    else videoRef.current.pause();
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current || isLocked) return;
    const t = videoRef.current.currentTime;
    setPlayed(t);
    if (!isWatched && !autoMarked && duration > 0 && t / duration >= 0.9) {
      setAutoMarked(true);
      onWatched();
    }
  };

  const handleVolume = (e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    if (videoRef.current) videoRef.current.volume = v;
  };

  const setRate = (rate) => {
    setPlaybackRate(rate);
    if (videoRef.current) videoRef.current.playbackRate = rate;
    setShowRateMenu(false);
  };

  const toggleFullscreen = () => {
    const el = videoRef.current?.parentElement;
    if (!document.fullscreenElement) el?.requestFullscreen();
    else document.exitFullscreen();
  };

  const skip = (s) => {
    if (videoRef.current && !isLocked) videoRef.current.currentTime += s;
  };

  const fmt = (secs) => {
    if (!isFinite(secs) || isNaN(secs)) return '00:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  return (
    <div
      className="fsp-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="fsp-overlay-inner">
        <div className="fsp-overlay-header">
          <div className="fsp-overlay-meta">
            <span className="fsp-overlay-counter">
              ویدیو {toPersianDigits(videoIndex + 1)} از{' '}
              {toPersianDigits(course.videos.length)}
            </span>
            <h2 className="fsp-overlay-title">{video.title}</h2>
          </div>
          <button className="fsp-overlay-close" onClick={onClose}>
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="fsp-player">
          <video
            ref={videoRef}
            src={video.url}
            className="fsp-video-el"
            onClick={handleVideoClick} // 🆕 فقط تاگل کنترل‌ها
            onPlay={() => { setIsPlaying(true); setControlsVisible(true); }} // 🆕
            onPause={() => { setIsPlaying(false); setControlsVisible(true); }} // 🆕
            onTimeUpdate={handleTimeUpdate}
            onProgress={handleProgress} // 🆕 دریافت بافر
            onLoadedMetadata={(e) => setDuration(e.target.duration)}
          />

          {isLocked && (
            <div className="fsp-player-lock-overlay">
              <div className="fsp-player-lock-icon">🔒</div>
              <div className="fsp-player-lock-title">این ویدیو قفل است</div>
              <div className="fsp-player-lock-sub">برای تماشا، آن را باز کنید</div>
              <button className="fsp-modal-btn fsp-modal-btn-primary" onClick={onRequestPurchase}>
                <i className="fas fa-unlock" /> باز کردن ویدیو
              </button>
            </div>
          )}

          {!isLocked && (
            <div 
              className={`fsp-controls ${controlsVisible ? 'is-visible' : 'is-hidden'}`}
              onMouseMove={resetHideTimer} // 🆕 ماندن نوار هنگام حرکت موس روی آن
            >
              <div className="fsp-progress-row">
                {/* 🆕 نوار پیشرفت سفارشی با قابلیت نمایش بافر */}
                <div 
                  className="fsp-progress-bar-container"
                  ref={progressBarRef}
                  onClick={handleProgressClick}
                >
                  <div 
                    className="fsp-progress-buffered" 
                    style={{ width: `${duration > 0 ? (buffered / duration) * 100 : 0}%` }} 
                  />
                  <div 
                    className="fsp-progress-played" 
                    style={{ width: `${duration > 0 ? (played / duration) * 100 : 0}%` }}
                  >
                    <div className="fsp-progress-thumb" />
                  </div>
                </div>
                <div className="fsp-time">
                  <span>{fmt(played)}</span>
                  <span>/</span>
                  <span>{fmt(duration)}</span>
                </div>
              </div>

              <div className="fsp-controls-row">
                <button className="fsp-ctrl" onClick={() => skip(-5)}>
                  <i className="fas fa-undo" /> ۵
                </button>
                <button className="fsp-ctrl fsp-ctrl-play" onClick={togglePlay}>
                  <i className={`fas fa-${isPlaying ? 'pause' : 'play'}`} />
                </button>
                <button className="fsp-ctrl" onClick={() => skip(5)}>
                  ۵ <i className="fas fa-redo" />
                </button>

                <div className="fsp-rate-wrap">
                  <button className="fsp-ctrl" onClick={() => setShowRateMenu(!showRateMenu)}>
                    {playbackRate}x
                  </button>
                  {showRateMenu && (
                    <div className="fsp-rate-menu">
                      {[0.5, 0.75, 1, 1.25, 1.5, 2].map((r) => (
                        <button
                          key={r}
                          className={playbackRate === r ? 'active' : ''}
                          onClick={() => setRate(r)}
                        >
                          {r}x
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="fsp-vol-wrap">
                  <button
                    className="fsp-ctrl"
                    onClick={() => {
                      const v = volume === 0 ? 0.8 : 0;
                      setVolume(v);
                      if (videoRef.current) videoRef.current.volume = v;
                    }}
                  >
                    <i className={`fas fa-volume-${volume === 0 ? 'mute' : 'up'}`} />
                  </button>
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.05}
                    value={volume}
                    onChange={handleVolume}
                    className="fsp-vol"
                  />
                </div>

                <button className="fsp-ctrl" onClick={toggleFullscreen}>
                  <i className="fas fa-expand" />
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="fsp-overlay-actions">
          <div className="fsp-overlay-actions-left">
            {!isLocked && (
              <button
                className={`fsp-btn ${isWatched ? 'fsp-btn-done' : 'fsp-btn-primary'}`}
                onClick={isWatched ? onUnwatch : onWatched}
              >
                <i className={`fas fa-${isWatched ? 'check-circle' : 'check'}`} />
                {isWatched ? 'دیده شده' : 'علامت گذاری به عنوان دیده شده'}
              </button>
            )}
          </div>
          <div className="fsp-overlay-actions-right">
            <button className="fsp-btn fsp-btn-ghost" onClick={onPrev} disabled={!hasPrev}>
              <i className="fas fa-chevron-right" /> قبلی
            </button>
            <button className="fsp-btn fsp-btn-ghost" onClick={onNext} disabled={!hasNext}>
              بعدی <i className="fas fa-chevron-left" />
            </button>
          </div>
        </div>

        {!isLocked && (
          <div className="fsp-note-section">
            <div className="fsp-note-head">
              <i className="fas fa-pencil-alt" />
              <span>یادداشت من</span>
              {noteSaved && (
                <span className="fsp-note-saved">
                  <i className="fas fa-check" /> ذخیره شد
                </span>
              )}
            </div>
            <textarea
              className="fsp-note-textarea"
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              placeholder="یادداشتهای خود را اینجا بنویسید..."
              rows={3}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// ================================================================
// 6) Confetti
// ================================================================
export function ConfettiCelebration({ course }) {
  const colors = ['#fbbf24', '#ec4899', '#8b5cf6', '#22c55e', '#3b82f6', course?.color || '#4f46e5'];
  const pieces = Array.from({ length: 30 }, (_, i) => i);

  return (
    <div className="fsp-confetti-layer">
      {pieces.map((i) => (
        <span
          key={i}
          className="fsp-confetti-piece"
          style={{
            '--x': `${(i * 3.3) % 100}%`,
            '--delay': `${(i % 10) * 0.12}s`,
            '--rot': `${(i * 37) % 360}deg`,
            '--color': colors[i % colors.length],
            '--dur': `${3 + (i % 3)}s`,
          }}
        />
      ))}
      <div className="fsp-celebrate-toast">
        <div className="fsp-celebrate-emoji">🎉</div>
        <div>
          <div className="fsp-celebrate-title">دوره را کامل کردی!</div>
          <div className="fsp-celebrate-sub">{course?.title}</div>
        </div>
      </div>
    </div>
  );
}